/** Only navigation labels and one live header registration, never documents. */
export type WorkspaceNavigationLabel = {
  id: string; title: string; icon: string | null; hasCover?: boolean;
  /** A palette token only; image URLs never enter navigation labels. */
  coverColor?: string;
  description?: string | null; updated_at?: string; store_ref?: string | null;
};
export type WorkspacePreviewHint = Pick<WorkspaceNavigationLabel, "description" | "updated_at" | "hasCover" | "coverColor">;
export const WORKSPACE_NAVIGATION_START = "frogstat:workspace-navigation-start";
export const WORKSPACE_PAGE_CREATION_START = "frogstat:workspace-page-creation-start";
let navigationVersion = 0;
export const workspaceNavigationVersion = () => navigationVersion;

/** Home has no document id and must never enter the activation/write queue. */
export function workspaceHomePath(href: string): "/" | "/home" | null {
  return href === "/" || href === "/home" ? href : null;
}

export function announceWorkspacePageCreation(): void {
  navigationVersion++;
  window.dispatchEvent(new Event(WORKSPACE_PAGE_CREATION_START));
}

export function workspacePageId(href: string): string | null {
  // Store switches, embeds and external links keep their existing navigation.
  const match = /^\/page\/([^/?#]+)\/?$/.exec(href);
  if (!match) return null;
  try { return decodeURIComponent(match[1]); } catch { return null; }
}

export function announceWorkspaceNavigation(href: string): void {
  const pageId = workspacePageId(href);
  // Other local destinations only cancel a superseded preview. They do not
  // gain document labels, speculative requests or activation/store writes.
  if (href.startsWith("/") && !/^\/[\\/]/.test(href)) {
    // Any actual local navigation supersedes a slow creation, including
    // settings/news. Hover and modified clicks do not call this function.
    navigationVersion++;
    window.dispatchEvent(new CustomEvent(WORKSPACE_NAVIGATION_START, { detail: { pageId, href } }));
  }
}

export function createWorkspaceNavigationShellStore<T>() {
  type Header = { lease: object; page: WorkspaceNavigationLabel; value: T };
  let labels = new Map<string, WorkspaceNavigationLabel>();
  let state: { pending: WorkspaceNavigationLabel | null; header: Header | null; pendingHome?: { pathname: "/" | "/home" } } = { pending: null, header: null };
  const listeners = new Set<() => void>();
  const emit = () => { for (const listener of listeners) listener(); };
  const withoutHome = () => { const { pendingHome: _home, ...rest } = state; return rest; };
  return {
    subscribe(listener: () => void) { listeners.add(listener); return () => { listeners.delete(listener); }; },
    getSnapshot: () => state,
    label: (id: string | null) => id ? labels.get(id) : undefined,
    remember(pages: readonly WorkspaceNavigationLabel[]) { labels = new Map(pages.map(page => [page.id, page])); },
    /** Called only with a successful creation POST in this shell's scope. */
    rememberCreatedPage(page: WorkspaceNavigationLabel) { labels.set(page.id, page); },
    rememberPreview(id: string, hint: WorkspacePreviewHint) {
      const page = labels.get(id);
      if (page) labels.set(id, { ...page, ...hint });
    },
    begin(id: string, routeId?: string | null) {
      const page = labels.get(id);
      if (!page) {
        // An unknown/deleted destination must not leave Home over its 404.
        // Never trust arbitrary link text as account metadata.
        if (state.pendingHome) { state = { ...withoutHome(), pending: null }; emit(); }
        return;
      }
      if (state.header?.page.id === id && routeId === id) {
        state = { ...withoutHome(), pending: null }; emit(); return;
      }
      state = { ...withoutHome(), pending: page }; emit();
    },
    beginHome(href: string, currentPathname: string) {
      const pathname = workspaceHomePath(href);
      if (!pathname) return;
      state = pathname === currentPathname
        ? { ...withoutHome(), pending: null }
        : { ...state, pending: null, pendingHome: { pathname } };
      emit();
    },
    publish(lease: object, page: WorkspaceNavigationLabel, value: T, routeId: string | null) {
      if (state.pendingHome || page.id !== routeId || (state.pending && state.pending.id !== page.id)) return;
      // A title-only publication must not erase the page's lightweight card.
      page = { ...labels.get(page.id), ...page };
      labels.set(page.id, page);
      state = { pending: null, header: { lease, page, value } }; emit();
    },
    release(lease: object) {
      if (state.header?.lease !== lease) return;
      state = { ...state, header: null }; emit();
    },
    cancel() { if (state.pending || state.pendingHome) { state = { ...withoutHome(), pending: null }; emit(); } },
  };
}
